import React, {ReactNode, useEffect, useState} from 'react';

import {Link} from 'react-router';

import {
  RoleAvatar,
  TeamAvatar,
  UserAvatar,
} from 'app/components/general/avatars';
import {GuestFileAccessLink} from 'app/mediaaccess/components/FileAccess';
import {getFilename} from 'app/mediaaccess/utils';
import {useAccountSettingsState} from 'app/settings/api';
import {gt} from 'app/utils/i18n';

import {DateText, FixableHeader, FixableTable} from './utils';

import type {
  DocTaskReportItemType,
  ReportFormFieldType,
  ReportResultSerializedType,
} from 'sweet-types';

interface Props {
  result: ReportResultSerializedType<DocTaskReportItemType>;
}

export function RenderDocumentInstance({result}: Props): JSX.Element {
  const [fieldHeadings, setFieldHeadings] = useState<Record<string, string>>(
    {}
  );
  const [formattedData, setFormattedData] = useState<Array<Array<ReactNode>>>(
    []
  );

  const {MEDIA_URL_PROTECTED} = useAccountSettingsState().data!;

  useEffect(() => {
    const formatData = () => {
      const newFieldHeadings = {};
      const formattedRows = result.results.map((row) => {
        const fixedRow = formatFixedColumns(row);
        const formRow = formatFieldColumns(
          row['form_fields'],
          newFieldHeadings
        );
        return [...fixedRow, ...formRow];
      });

      setFieldHeadings(newFieldHeadings);
      setFormattedData(formattedRows);
    };

    formatData();
  }, [result]);

  const formatFixedColumns = (row: DocTaskReportItemType) => [
    renderLink({text: row['name'], url: row.html_url}),
    renderDate(row.due),
    renderAssignees(row.users, row.teams, row.roles),
    renderDate(row.completed_at),
    row.completed_by ? <UserAvatar user={row.completed_by} /> : null,
  ];

  function renderAssignees(users, teams, roles) {
    return (
      <div className="d-flex align-items-center">
        {users.map((user) => (
          <UserAvatar
            key={user.url}
            user={user}
            small
            showCard
            isGuest={user.is_guest_until}
            className="mr-1"
          />
        ))}
        {teams.map((team) => (
          <TeamAvatar key={team.url} team={team} small link className="mr-1" />
        ))}
        {(roles ?? []).map((role) => (
          <RoleAvatar key={role.url} role={role} small link className="mr-1" />
        ))}
      </div>
    );
  }

  const formatFieldColumns = (
    fields: Array<ReportFormFieldType>,
    newFieldHeadings: Record<string, ReactNode>
  ) => {
    const fieldRow: {
      [k: string]: ReactNode;
    } = Object.fromEntries(
      Object.keys(newFieldHeadings).map((key) => [key, ''])
    );

    fields.forEach((field) => {
      newFieldHeadings[field.id] = field.label;
      fieldRow[field.id] = formatFieldValue(field);
    });

    return Object.values(fieldRow);
  };

  const formatFieldValue = (form: ReportFormFieldType) => {
    if (form.type === 'date') {
      return renderDate(form.value);
    } else if (form.type === 'file') {
      return renderFile(form.value);
    } else {
      return form.value;
    }
  };

  const renderDate = (date) => <DateText>{date}</DateText>;

  const renderFile = (url?: string) => {
    // form the url is a full absolute path to cloudfront,
    // hence we split the pathname and get the required part
    if (url == null) {
      return null;
    }

    const urlParts = url.split('/site_media/media/');
    const fileUrl = urlParts.length > 1 ? urlParts[1] : '';
    return (
      <GuestFileAccessLink
        MEDIA_URL_PROTECTED={MEDIA_URL_PROTECTED}
        file_ref={fileUrl}
        file_name={getFilename(url)}
      />
    );
  };

  const renderLink = ({text, url}: {text: string; url: string}) => (
    <Link className="font-weight-bold" to={url}>
      {text}
    </Link>
  );

  return (
    <FixableTable>
      <thead>
        <tr>
          <FixableHeader>{gt('Task')}</FixableHeader>
          <FixableHeader>{gt('Due')}</FixableHeader>
          <FixableHeader>{gt('Assigned To')}</FixableHeader>
          <FixableHeader>{gt('Completed')}</FixableHeader>
          <FixableHeader>{gt('Completed By')}</FixableHeader>
          {Object.values(fieldHeadings).map((heading, index) => (
            <FixableHeader key={index}>{heading}</FixableHeader>
          ))}
        </tr>
      </thead>
      <tbody>
        {formattedData.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </FixableTable>
  );
}
